import React, { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box,
  Alert,
  Snackbar,
  Typography,
  Zoom,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Chat, Close, Send } from '@mui/icons-material';
import { submitContactForm } from '../../services/api';

const FloatingChat = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phoneNumber.trim() || !formData.message.trim()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error',
      });
      return;
    }

    setLoading(true);

    const result = await submitContactForm(formData);

    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Message sent successfully! We will get back to you soon.',
        severity: 'success',
      });
      setFormData({ name: '', phoneNumber: '', message: '' });
      setOpen(false);
    } else {
      setSnackbar({
        open: true,
        message: result.error,
        severity: 'error',
      });
    }

    setLoading(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Zoom in>
        <Fab
          color="secondary"
          aria-label="Chat with us"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, md: 32 },
            right: { xs: 24, md: 32 },
            zIndex: 1200,
            width: 56,
            height: 56,
            boxShadow: '0 4px 20px rgba(201, 169, 97, 0.4)',
            '&:hover': {
              boxShadow: '0 6px 28px rgba(201, 169, 97, 0.6)',
            },
          }}
        >
          <Chat />
        </Fab>
      </Zoom>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Get In Touch
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Send us a message and we'll get back to you
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}
          >
            <TextField
              name="name"
              label="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              placeholder="Enter your full name"
            />

            <TextField
              name="phoneNumber"
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              fullWidth
              variant="outlined"
              placeholder="+254 7XX XXX XXX"
              type="tel"
            />

            <TextField
              name="message"
              label="Your Message"
              value={formData.message}
              onChange={handleChange}
              required
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="How can we help you?"
            />

            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              loading={loading}
              loadingPosition="end"
              endIcon={<Send />}
              sx={{ py: 1.5 }}
            >
              Send Message
            </LoadingButton>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FloatingChat;
